/* Users.js - Main component */
import React, {
  useState,
  useEffect,
  Fragment,
  useMemo,
  useCallback,
} from "react";
import { Button, Spinner } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { getUsers } from "ServerAPI/serverAPI";
import { TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ConfirmationDialog from "components/Dialog/ConfirmationDialog";
import ToastMessage from "components/Dialog/ToastMessage";
import _ from "lodash";
import RefreshButton from "components/elements/refresh-button/RefreshButton";
import { ROLES } from "constants/Constants";
import EditUserModal from "./EditUserModal";
import AddUserModal from "components/users/add-new-user";
import UsersTable from "./UsersTable";

const Users = ({ allDepartments, organizationDetails }) => {
  const [cookies] = useCookies();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [showUserDeleteConfirmationDialog, setShowUserDeleteConfirmationDialog] = useState(false);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [isUserDeleted, setIsUserDeleted] = useState(false);
  const [userForEdit, setUserForEdit] = useState({});
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [usersWithoutRole, setUsersWithoutRole] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  let clickedUserDetails = useMemo(() => {
    return {};
  }, []);

  // Table columns definition
  const columns = useMemo(() => {
    if (cookies.userManagement === "HRMS") {
      return [
        {
          accessorKey: "id",
          id: 1,
          header: () => (
            <span style={{ fontWeight: "bold", color: "#224D99" }}>SN</span>
          ),
        },
        {
          accessorKey: "userId",
          id: 9,
          header: () => (
            <span style={{ fontWeight: "bold", color: "#224D99" }}>Emp ID</span>
          ),
        },
        {
          accessorKey: "actions",
          id: 8,
          header: () => (
            <span style={{ fontWeight: "bold", color: "#224D99" }}>Actions</span>
          ),
        },
      ];
    }

    return [
      {
        accessorKey: "id",
        id: 1,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>SN</span>
        ),
      },
      {
        accessorKey: "name",
        id: 2,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Name</span>
        ),
      },
      {
        accessorKey: "email",
        id: 3,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Email</span>
        ),
      },
      {
        accessorKey: "phone",
        id: 4,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Phone</span>
        ),
      },
      {
        accessorKey: "department",
        id: 5,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>
            Department
          </span>
        ),
      },
      {
        accessorKey: "role",
        id: 6,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Role</span>
        ),
      },
      {
        accessorKey: "status",
        id: 7,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Status</span>
        ),
      },
      {
        accessorKey: "actions",
        id: 8,
        header: () => (
          <span style={{ fontWeight: "bold", color: "#224D99" }}>Actions</span>
        ),
      },
    ];
  }, [cookies.userManagement]);

  // Fetch users data
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers(
        cookies.email,
        cookies.roleId,
        cookies.organizationId
      );

      const formattedData = _.compact(
        response.data?.users?.map((user, index) => {
          if (user.role) {
            return {
              id: index + 1,
              name: user.name,
              email: user.email,
              role: user.role.roleName,
              roleId: user.role.roleId,
              phone: user.phoneNumber,
              department: user?.department,
              userId: user?.userId,
              deleted: user?.deleted,
              status: user?.deleted ? "inactive" : "active",
              features: user?.features,
            };
          }
        })
      );

      const withoutRole = _.compact(
        response.data?.users?.map((user, index) => {
          if (!user.role) {
            return {
              label: user.name,
              value: user?.userId,
            };
          }
        })
      );

      setUsersWithoutRole(withoutRole);
      setData(formattedData);
      setAllUsers(formattedData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [cookies]);

  // Handle modal close
  const closeAddUserModal = () => {
    setOpenAddUserModal(false);
  };

  // Handle search functionality
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsers();
    setIsRefreshing(false);
  };

  // Filter data based on search
  const filteredData = allUsers.filter((user) => {
    return (
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Delete confirmation handlers
  const closeUserDeleteConfirmationDialog = useCallback(() => {
    setShowUserDeleteConfirmationDialog(false);
  }, []);

  const closeToastMessage = () => {
    setIsUserDeleted(false);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search for user"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <SearchIcon /> }}
        />
        <div className="d-flex align-items-center gap-4">
          <RefreshButton
            handleRefresh={handleRefresh}
            title="Refresh Users"
            isRefreshing={isRefreshing}
          />
          <Button onClick={() => setOpenAddUserModal(true)}>
            + Add User
          </Button>
        </div>
      </div>

      <Fragment>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" size="lg" />
          </div>
        ) : (
          <UsersTable
            columns={columns}
            data={filteredData}
            setData={setData}
            fetchUsers={fetchUsers}
            userForEdit={userForEdit}
            setUserForEdit={setUserForEdit}
            openEditUserModal={openEditUserModal}
            setOpenEditUserModal={setOpenEditUserModal}
            organizationDetails={organizationDetails}
            allDepartments={allDepartments}
            cookies={cookies}
          />
        )}
      </Fragment>

      {/* Add User Modal - handles both normal and guest users */}
      <AddUserModal
        fetchUsers={fetchUsers}
        open={openAddUserModal}
        handleClose={closeAddUserModal}
        departments={allDepartments}
        organizationDetails={organizationDetails}
        usersWithoutRole={usersWithoutRole}
        cookies={cookies}
      />

      {/* Edit User Modal */}
      <EditUserModal
        open={openEditUserModal}
        handleClose={() => setOpenEditUserModal(false)}
        userForEdit={userForEdit}
        organizationDetails={organizationDetails}
        allDepartments={allDepartments}
        fetchUsers={fetchUsers}
        cookies={cookies}
      />

      {/* Delete confirmation modal */}
      <ConfirmationDialog
        open={showUserDeleteConfirmationDialog}
        handleClose={closeUserDeleteConfirmationDialog}
        message="Are you sure to delete the user"
        isLoading={deleteUserLoading}
      />

      {/* Success toast message */}
      <ToastMessage
        open={isUserDeleted}
        message="User Deleted Successfully"
        closeTiming={3000}
        handleClose={closeToastMessage}
      />
    </div>
  );
};

export default Users;